(() => {
  // Compare analytics are queued through dataLayer and must not block product interactions.
  const TAG_NAME = 'vl-product-compare-manager';
  const STORAGE_KEY = 'vl_product_compare_v1';
  const LEGACY_STORAGE_KEY = 'compare_list';
  const STORAGE_VERSION = 1;
  const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
  const MAX_PRODUCTS = 3;
  const MIN_PRODUCTS = 2;
  const OPTION_ALIASES = {
    style: ['style', 'frame style', 'frame type'],
    size: ['size', 'frame size'],
    color: ['color', 'colour']
  };

  if (customElements.get(TAG_NAME)) return;

  function normalizeText(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function uniqueEntries(entries) {
    const ids = new Set();
    const handles = new Set();

    return (Array.isArray(entries) ? entries : []).filter((entry) => {
      const id = String(entry?.id || '');
      const handle = String(entry?.handle || '');
      if (!id || !handle || ids.has(id) || handles.has(handle)) return false;
      ids.add(id);
      handles.add(handle);
      return true;
    });
  }

  function uniqueHandles(handles) {
    return Array.from(new Set((Array.isArray(handles) ? handles : []).filter(Boolean))).slice(0, MAX_PRODUCTS);
  }

  function normalizeSpecs(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value !== 'string') return {};
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function normalizeEntry(entry) {
    if (!entry) return null;
    const id = String(entry.id || entry.product?.id || '');
    const handle = String(entry.handle || entry.product?.handle || '');
    if (!id || !handle) return null;
    return { id, handle };
  }

  function optionField(name) {
    const normalizedName = normalizeText(name);
    return Object.keys(OPTION_ALIASES).find((field) => (
      OPTION_ALIASES[field].includes(normalizedName)
    )) || null;
  }

  function optionEntries(product) {
    const names = Array.isArray(product?.options)
      ? product.options.map((option) => typeof option === 'string' ? option : option?.name)
      : [];

    return names.map((name, index) => ({ field: optionField(name), index }))
      .filter((entry) => entry.field);
  }

  function optionSchema(product) {
    return optionEntries(product).map((entry) => entry.field);
  }

  function variantSelection(product, variant) {
    const values = Array.isArray(variant?.options)
      ? variant.options
      : [variant?.option1, variant?.option2, variant?.option3];

    return optionEntries(product).reduce((selection, entry) => {
      if (values[entry.index] != null) selection[entry.field] = values[entry.index];
      return selection;
    }, {});
  }

  function variantMatches(product, variant, selection) {
    const current = variantSelection(product, variant);
    return optionSchema(product).every((field) => (
      selection[field] == null || current[field] === selection[field]
    ));
  }

  function resolveVariant(product, selection = {}, changedField = null) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return { variant: null, selection: {}, exact: false };

    const exactVariant = variants.find((variant) => variantMatches(product, variant, selection));
    const schema = optionSchema(product);
    const candidates = changedField
      ? variants.filter((variant) => variantSelection(product, variant)[changedField] === selection[changedField])
      : variants;
    const fallbackVariant = candidates
      .map((variant) => ({
        variant,
        score: schema.reduce((score, field) => (
          field !== changedField && variantSelection(product, variant)[field] === selection[field]
            ? score + 1
            : score
        ), 0)
      }))
      .sort((left, right) => right.score - left.score)[0]?.variant;
    const variant = exactVariant || fallbackVariant || variants[0];

    return {
      variant,
      selection: variantSelection(product, variant),
      exact: Boolean(exactVariant)
    };
  }

  function defaultVariant(product, defaultVariantId = '') {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const selectedId = String(defaultVariantId || '');
    return variants.find((variant) => String(variant.id) === selectedId)
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
  }

  function parseProductsParam(search = window.location.search) {
    const params = new URLSearchParams(search);
    const handles = String(params.get('products') || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    return Array.from(new Set(handles)).slice(0, MAX_PRODUCTS);
  }

  function compareUrl(basePath, products) {
    const url = new URL(basePath || '/pages/compare-velotric-ebikes', window.location.origin);
    url.searchParams.set('products', products.map((product) => product.handle).join(','));
    return `${url.pathname}${url.search}`;
  }

  class VLProductCompareManager extends HTMLElement {
    static readStoredHistory() {
      try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (
          parsed?.version === STORAGE_VERSION
          && Array.isArray(parsed.products)
          && Number(parsed.expiresAt) > Date.now()
        ) {
          return uniqueEntries(parsed.products.map(normalizeEntry)).slice(0, MAX_PRODUCTS);
        }
      } catch (error) {
        return [];
      }
      return [];
    }

    static writeStoredHistory(products, existingHistory = this.readStoredHistory()) {
      const operatedEntries = (Array.isArray(products) ? products : [products])
        .map(normalizeEntry)
        .filter(Boolean);
      const next = uniqueEntries([...operatedEntries, ...existingHistory]).slice(0, MAX_PRODUCTS);
      const updatedAt = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        products: next,
        updatedAt,
        expiresAt: updatedAt + STORAGE_TTL
      }));
      return next;
    }

    static compareUrl(basePath, products) {
      return compareUrl(basePath, products);
    }

    static pushAnalytics(eventName, options = {}) {
      try {
        const products = Array.isArray(options.products) ? options.products.slice(0, MAX_PRODUCTS) : [];
        const handles = products.map((product) => product?.handle || null);
        while (handles.length < MAX_PRODUCTS) handles.push(null);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'ga4Event',
          event_name: eventName,
          event_parameters: {
            compare_context: options.context || null,
            product_count: products.filter((product) => product?.handle).length,
            product_1_handle: handles[0],
            product_2_handle: handles[1],
            product_3_handle: handles[2],
            operated_product_handle: options.operatedProductHandle || null,
            previous_product_handle: options.previousProductHandle || null,
            operated_slot: Number.isInteger(options.operatedSlot) ? options.operatedSlot : null,
            initial_source: options.initialSource || null,
            action: options.action || null
          }
        });
      } catch (error) {
        // Analytics must never interrupt the comparison experience.
      }
    }

    constructor() {
      super();
      this.abortController = null;
      this.requestVersion = 0;
      this.products = [];
      this.productRequests = new Map();
      this.productCache = new Map();
      this.candidates = null;
      this.candidatesPromise = null;
      this.candidatePrefetchTimer = null;
      this.history = [];
      this.defaultHandles = [];
      this.relatedHandles = [];
      this.currentHandle = '';
      this.mode = 'page';
      this.initialSource = null;
      this.viewTracked = false;
      this.pdpTrackingPending = false;
      this.stickyExploreEnabled = false;
      this.basePath = '/pages/compare-velotric-ebikes';
      this.endpointTemplate = '/products/{handle}?view=compare-data';
      this.candidateEndpoint = '/collections/electric-bikes?view=compare-data';
      this.swatchPatterns = '';
      this.labels = {};
      this.specDefinitions = [];
      this.headerObserver = null;
      this.stickyOffsetFrame = null;
      this.stickyOffsetSyncUntil = 0;
      this.selectorResizeObserver = null;
      this.specNavObserver = null;
      this.specNavTrackingTarget = null;
      this.specNavTrackingHandler = null;
      this.specNavTrackingFrame = null;
      this.specNavAtEnd = false;
      this.vlScrollAreas = null;
      this.vlScrollSyncFrame = null;
      this.vlScrollSyncLeft = 0;
      this.vlScrollSyncSource = null;
      this.vlScrollVisibilityObserver = null;
      this.vlWheelAxis = null;
      this.vlWheelAxisTimer = null;
      this.vlScrollResizeBound = false;
      this.vlScrollResizeTimer = null;
      this.pendingPdpActivation = false;
      this.legacyProductIds = [];
      this.handleStorage = this.handleStorage.bind(this);
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
      this.updateSelectorStickyState = this.updateSelectorStickyState.bind(this);
      this.syncStickyOffsetDuringTransition = this.syncStickyOffsetDuringTransition.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.readConfig();
      this.history = this.readHistory();
      window.addEventListener('storage', this.handleStorage, { signal: this.abortController.signal });
      window.addEventListener('resize', this.updateStickyOffset, { signal: this.abortController.signal });
      window.addEventListener('scroll', this.updateStickyOffset, {
        signal: this.abortController.signal,
        passive: true
      });
      this.closest('#compare_popup')?.querySelector('.popup__content-container')?.addEventListener(
        'scroll',
        this.updateStickyOffset,
        { signal: this.abortController.signal, passive: true }
      );
      this.observeHeader();
      this.observeSelectorStickyHeight();
      this.initialize().finally(() => this.scheduleCandidatePrefetch());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.headerObserver?.disconnect();
      this.headerObserver = null;
      if (this.stickyOffsetFrame != null) cancelAnimationFrame(this.stickyOffsetFrame);
      this.stickyOffsetFrame = null;
      if (this.vlScrollSyncFrame != null) cancelAnimationFrame(this.vlScrollSyncFrame);
      this.vlScrollSyncFrame = null;
      this.vlScrollAreas = null;
      this.vlScrollSyncSource = null;
      this.vlScrollSyncLeft = 0;
      this.vlScrollVisibilityObserver?.disconnect();
      this.vlScrollVisibilityObserver = null;
      window.clearTimeout(this.vlWheelAxisTimer);
      this.vlWheelAxis = null;
      this.vlWheelAxisTimer = null;
      window.clearTimeout(this.vlScrollResizeTimer);
      this.vlScrollResizeBound = false;
      this.vlScrollResizeTimer = null;
      this.stickyOffsetSyncUntil = 0;
      this.selectorResizeObserver?.disconnect();
      this.selectorResizeObserver = null;
      this.specNavObserver?.disconnect();
      this.specNavObserver = null;
      this.specNavTrackingTarget?.removeEventListener('scroll', this.specNavTrackingHandler);
      this.specNavTrackingTarget = null;
      this.specNavTrackingHandler = null;
      if (this.specNavTrackingFrame != null) cancelAnimationFrame(this.specNavTrackingFrame);
      this.specNavTrackingFrame = null;
      this.specNavAtEnd = false;
      if (this.candidatePrefetchTimer != null) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.candidatePrefetchTimer);
        else window.clearTimeout(this.candidatePrefetchTimer);
      }
      this.candidatePrefetchTimer = null;
      this.productRequests.clear();
      this.candidatesPromise = null;
      this.requestVersion += 1;
    }

    readConfig() {
      const configElement = this.querySelector('[data-vl-product-compare-config]');
      if (!configElement) return;

      try {
        const config = JSON.parse(configElement.textContent || '{}');
        this.defaultHandles = Array.isArray(config.defaultHandles)
          ? config.defaultHandles.filter(Boolean).slice(0, MAX_PRODUCTS)
          : [];
        this.relatedHandles = Array.isArray(config.relatedHandles)
          ? config.relatedHandles.filter(Boolean).slice(0, MAX_PRODUCTS - 1)
          : [];
        this.currentHandle = config.currentHandle || '';
        this.mode = config.mode === 'pdp' ? 'pdp' : 'page';
        this.stickyExploreEnabled = this.mode === 'page' && config.stickyExploreEnabled === true;
        this.basePath = config.basePath || this.basePath;
        this.endpointTemplate = config.endpointTemplate || this.endpointTemplate;
        this.candidateEndpoint = config.candidateEndpoint || this.candidateEndpoint;
        this.swatchPatterns = typeof config.swatchPatterns === 'string' ? config.swatchPatterns : '';
        this.labels = config.labels && typeof config.labels === 'object' ? config.labels : {};
        this.specDefinitions = Array.isArray(config.specDefinitions) ? config.specDefinitions : [];
      } catch (error) {
        this.emitError('invalid-config', error);
      }
    }

    async initialize() {
      if (!this.history.length && this.legacyProductIds.length) {
        await this.migrateLegacyHistory();
      }

      if (this.mode === 'pdp') {
        await this.initializePdp();
        return;
      }

      const urlHandles = parseProductsParam();
      const historyHandles = this.history.map((entry) => entry.handle);
      const sources = [
        { name: 'url', handles: urlHandles },
        { name: 'history', handles: historyHandles },
        { name: 'default', handles: this.defaultHandles }
      ].filter((source) => source.handles.length >= MIN_PRODUCTS);

      for (const source of sources) {
        const loaded = await this.setHandles(source.handles, {
          source: source.name,
          initialSource: source.name,
          updateHistory: false,
          updateUrl: this.hasAttribute('data-sync-url'),
          suppressInsufficientError: source !== sources.at(-1)
        });
        if (loaded) return;
      }

      this.emitError('insufficient-products');
    }

    async initializePdp() {
      if (!this.currentHandle) {
        this.emitError('missing-current-product');
        return;
      }

      const historyHandles = this.history.map((entry) => entry.handle);
      const sources = [
        historyHandles.length ? {
          name: 'history',
          handles: uniqueHandles([
            this.currentHandle,
            ...historyHandles,
            ...(this.relatedHandles.length ? this.relatedHandles : this.defaultHandles)
          ])
        } : null,
        this.relatedHandles.length ? {
          name: 'pdp_related',
          handles: uniqueHandles([this.currentHandle, ...this.relatedHandles])
        } : null,
        {
          name: 'pdp_default',
          handles: uniqueHandles([this.currentHandle, ...this.defaultHandles])
        }
      ].filter(Boolean).filter((source, index, all) => (
        source.handles.length >= MIN_PRODUCTS
        && all.findIndex((candidate) => candidate.handles.join(',') === source.handles.join(',')) === index
      ));

      for (const source of sources) {
        const loaded = await this.setHandles(source.handles, {
          source: 'pdp-initialize',
          initialSource: source.name,
          updateHistory: false,
          updateUrl: false,
          suppressInsufficientError: source !== sources.at(-1)
        });
        if (loaded) return;
      }

      this.emitError('insufficient-products');
    }

    activatePdp() {
      if (this.mode !== 'pdp') return;
      this.pdpTrackingPending = true;
      const currentProduct = this.products.find((product) => product.handle === this.currentHandle);
      if (currentProduct) {
        this.commitHistory([currentProduct]);
      } else {
        this.pendingPdpActivation = true;
      }
      this.emitChange('pdp-open');
    }

    observeHeader() {
      const header = document.querySelector('.shopify-section-header');
      if (!header) return;
      this.headerObserver = new MutationObserver(this.syncStickyOffsetDuringTransition);
      this.headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'visible']
      });
      header.addEventListener('transitionrun', this.syncStickyOffsetDuringTransition, {
        signal: this.abortController.signal
      });
      header.addEventListener('transitionend', this.updateStickyOffset, {
        signal: this.abortController.signal
      });
      this.updateStickyOffset();
    }

    syncStickyOffsetDuringTransition() {
      this.stickyOffsetSyncUntil = performance.now() + 400;
      if (this.stickyOffsetFrame != null) return;

      const sync = (timestamp) => {
        this.updateStickyOffset();
        if (timestamp < this.stickyOffsetSyncUntil) {
          this.stickyOffsetFrame = requestAnimationFrame(sync);
        } else {
          this.stickyOffsetFrame = null;
        }
      };

      this.stickyOffsetFrame = requestAnimationFrame(sync);
    }

    observeSelectorStickyHeight() {
      const selectorsSticky = this.querySelector('[data-vl-product-compare-selectors-sticky]');
      if (!selectorsSticky) return;

      const updateHeight = () => {
        const height = Math.ceil(selectorsSticky.getBoundingClientRect().height);
        if (height > 0) {
          this.style.setProperty('--vl-product-compare-selector-sticky-height', `${height}px`);
        }
        const popupScroller = this.mode === 'pdp'
          ? this.closest('#compare_popup')?.querySelector('.popup__content-container')
          : null;
        const stickyOffset = popupScroller
          ? popupScroller.getBoundingClientRect().top
          : Number.parseFloat(
            getComputedStyle(this).getPropertyValue('--vl-product-compare-sticky-offset')
          ) || 0;
        this.updateSelectorStickyState(stickyOffset);
      };

      updateHeight();
      if (typeof ResizeObserver !== 'function') return;
      this.selectorResizeObserver = new ResizeObserver(updateHeight);
      this.selectorResizeObserver.observe(selectorsSticky);
    }

    updateStickyOffset() {
      if (this.mode === 'pdp') {
        const popupScroller = this.closest('#compare_popup')?.querySelector('.popup__content-container');
        const stickyViewportTop = popupScroller?.getBoundingClientRect().top || 0;
        this.style.setProperty('--vl-product-compare-sticky-offset', '0px');
        this.updateSelectorStickyState(stickyViewportTop);
        return;
      }

      const header = document.querySelector('.shopify-section-header');
      if (!header) {
        this.style.setProperty('--vl-product-compare-sticky-offset', '0px');
        this.updateSelectorStickyState(0);
        return;
      }
      const rect = header.getBoundingClientRect();
      const isVisible = header.getAttribute('aria-hidden') !== 'true' && rect.bottom > 0;
      const stickyOffset = isVisible ? Math.max(0, rect.bottom) : 0;
      this.style.setProperty('--vl-product-compare-sticky-offset', `${stickyOffset}px`);
      this.updateSelectorStickyState(stickyOffset);
    }

    updateSelectorStickyState(stickyOffset = 0) {
      const selectorsSticky = this.querySelector('[data-vl-product-compare-selectors-sticky]');
      if (!selectorsSticky) return;

      const rect = selectorsSticky.getBoundingClientRect();
      const isStuck = rect.height > 0
        && rect.top <= stickyOffset + 2
        && rect.bottom > stickyOffset;
      selectorsSticky.classList.toggle('is-stuck', isStuck);

      const stickyActions = this.querySelector('[data-vl-product-compare-sticky-actions-scroll]');
      if (stickyActions) {
        const selectorsScroll = selectorsSticky.querySelector('.vl-product-compare__selectors-scroll');
        const productsScroll = this.querySelector('.vl-product-compare__products-scroll');
        const stickyStyles = getComputedStyle(selectorsSticky);
        const selectorStackBottom = stickyOffset
          + (selectorsScroll?.getBoundingClientRect().height || 0)
          + (Number.parseFloat(stickyStyles.paddingTop) || 0)
          + (Number.parseFloat(stickyStyles.paddingBottom) || 0);
        const productsHavePassed = Boolean(
          productsScroll && productsScroll.getBoundingClientRect().bottom <= selectorStackBottom + 1
        );
        const showStickyActions = this.stickyExploreEnabled && isStuck && productsHavePassed;
        if (showStickyActions && stickyActions.hidden) {
          stickyActions.hidden = false;
          stickyActions.scrollLeft = this.vlScrollSyncLeft || 0;
        } else if (!showStickyActions && !stickyActions.hidden) {
          stickyActions.hidden = true;
        }
        this.querySelectorAll('.vl-product-compare__products .vl-product-compare__explore').forEach((link) => {
          if (showStickyActions) {
            link.setAttribute('tabindex', '-1');
            link.setAttribute('aria-hidden', 'true');
          } else {
            link.removeAttribute('tabindex');
            link.removeAttribute('aria-hidden');
          }
        });
      }

      const stickyHeight = Math.ceil(selectorsSticky.getBoundingClientRect().height);
      if (stickyHeight > 0) {
        this.style.setProperty('--vl-product-compare-selector-sticky-height', `${stickyHeight}px`);
      }
    }

    async setHandles(handles, options = {}) {
      const version = ++this.requestVersion;
      this.setAttribute('aria-busy', 'true');

      try {
        const settledResults = await Promise.allSettled(
          Array.from(new Set(handles)).slice(0, MAX_PRODUCTS).map((handle) => this.fetchProduct(handle))
        );
        if (version !== this.requestVersion) return;

        const results = settledResults
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value);
        const products = results.filter((item) => item?.product);
        if (products.length < MIN_PRODUCTS) {
          if (!options.suppressInsufficientError) this.emitError('insufficient-products');
          return false;
        }

        this.products = products.map((item) => this.createProductState(item));
        if (options.initialSource) this.initialSource = options.initialSource;

        if (this.pendingPdpActivation) {
          const currentProduct = this.products.find((product) => product.handle === this.currentHandle);
          if (currentProduct) this.commitHistory([currentProduct]);
          this.pendingPdpActivation = false;
        }

        if (options.historyProductHandle) {
          const operatedProduct = this.products.find((product) => (
            product.handle === options.historyProductHandle
          ));
          if (operatedProduct) this.commitHistory([operatedProduct]);
        } else if (options.updateHistory) {
          this.commitHistory(this.products);
        }
        if (options.updateUrl) this.replaceUrl();
        this.emitChange(options.source || 'set-products');
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') this.emitError('product-load-failed', error);
        return false;
      } finally {
        if (version === this.requestVersion) this.removeAttribute('aria-busy');
      }
    }

    async replaceProduct(index, handle) {
      if (index < 0 || index >= MAX_PRODUCTS || !handle) return;
      if (this.mode === 'pdp' && index === 0) return;
      if (this.products.some((product, productIndex) => product.handle === handle && productIndex !== index)) return;
      if (this.products[index]?.handle === handle) return true;
      const previousProductHandle = this.products[index]?.handle || null;

      const version = ++this.requestVersion;
      this.setAttribute('aria-busy', 'true');
      try {
        const item = await this.fetchProduct(handle);
        if (version !== this.requestVersion || !item?.product) return false;

        const nextProducts = [...this.products];
        nextProducts[index] = this.createProductState(item);
        this.products = nextProducts;
        this.commitHistory([nextProducts[index]]);
        if (this.hasAttribute('data-sync-url')) this.replaceUrl();
        this.emitChange('replace-product', {
          previousProductHandle,
          operatedProductHandle: nextProducts[index].handle,
          operatedSlot: index + 1
        });
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') this.emitError('product-load-failed', error);
        return false;
      } finally {
        if (version === this.requestVersion) this.removeAttribute('aria-busy');
      }
    }

    async getCandidates() {
      if (this.candidates) return this.candidates;
      if (this.candidatesPromise) return this.candidatesPromise;

      this.candidatesPromise = fetch(this.candidateEndpoint, {
        headers: { Accept: 'text/html' },
        signal: this.abortController?.signal
      }).then(async (response) => {
        if (!response.ok) throw new Error('Unable to load comparison candidates');
        const payload = await response.json();
        this.candidates = Array.isArray(payload?.products) ? payload.products : [];
        return this.candidates;
      }).catch((error) => {
        this.candidatesPromise = null;
        throw error;
      });
      return this.candidatesPromise;
    }

    scheduleCandidatePrefetch() {
      if (this.candidates || this.candidatesPromise || !this.isConnected) return;
      const prefetch = () => {
        this.candidatePrefetchTimer = null;
        this.getCandidates().catch(() => {});
      };
      if ('requestIdleCallback' in window) {
        this.candidatePrefetchTimer = window.requestIdleCallback(prefetch, { timeout: 3000 });
      } else {
        this.candidatePrefetchTimer = window.setTimeout(prefetch, 1500);
      }
    }

    createProductState(item) {
      const variant = defaultVariant(item.product, item.defaultVariantId);
      return {
        id: String(item.product.id),
        handle: item.product.handle,
        fullName: item.product.title,
        shortName: item.shortName || item.product.title,
        product: item.product,
        specs: normalizeSpecs(item.specs),
        variant,
        selection: variantSelection(item.product, variant),
        optionSchema: optionSchema(item.product),
        optionIndexes: optionEntries(item.product).reduce((indexes, entry) => {
          indexes[entry.field] = entry.index;
          return indexes;
        }, {}),
        swatchPatterns: this.swatchPatterns
      };
    }

    selectVariantOption(productId, field, value) {
      const productState = this.products.find((item) => item.id === String(productId));
      if (!productState || !productState.optionSchema.includes(field)) return null;

      const result = resolveVariant(productState.product, {
        ...productState.selection,
        [field]: value
      }, field);
      if (!result.variant) return null;

      productState.variant = result.variant;
      productState.selection = result.selection;
      this.emitChange('variant-option', {
        productId: productState.id,
        optionField: field,
        variantId: String(result.variant.id || '')
      });
      return result;
    }

    async fetchProduct(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      if (this.productRequests.has(handle)) return this.productRequests.get(handle);

      const endpoint = this.endpointTemplate.replace('{handle}', encodeURIComponent(handle));
      const request = fetch(endpoint, {
        headers: { Accept: 'text/html' },
        signal: this.abortController?.signal
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Unable to load comparison product: ${handle}`);
        const payload = await response.json();
        this.productCache.set(handle, payload);
        this.productRequests.delete(handle);
        return payload;
      }).catch((error) => {
        this.productRequests.delete(handle);
        throw error;
      });
      this.productRequests.set(handle, request);
      return request;
    }

    readHistory() {
      try {
        const storedHistory = this.constructor.readStoredHistory();
        if (storedHistory.length) return storedHistory;

        localStorage.removeItem(STORAGE_KEY);
        const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || 'null');
        this.legacyProductIds = Array.isArray(legacy) ? legacy.map(String).slice(0, MAX_PRODUCTS) : [];
      } catch (error) {
        this.emitError('history-read-failed', error);
      }
      return [];
    }

    async migrateLegacyHistory() {
      try {
        const candidates = await this.getCandidates();
        const migrated = this.legacyProductIds
          .map((id) => candidates.find((candidate) => String(candidate.id) === id))
          .filter(Boolean)
          .map((candidate) => ({ id: candidate.id, handle: candidate.handle }));
        if (migrated.length) {
          this.history = this.constructor.writeStoredHistory(migrated, []);
          this.dispatchEvent(new CustomEvent('vl:product-compare-legacy-history', {
            bubbles: true,
            detail: { products: this.history }
          }));
        }
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        this.legacyProductIds = [];
      } catch (error) {
        this.emitError('legacy-history-migration-failed', error);
      }
    }

    commitHistory(products = this.products) {
      try {
        this.history = this.constructor.writeStoredHistory(products, this.history);
      } catch (error) {
        this.emitError('history-write-failed', error);
      }
      return this.history;
    }

    handleStorage(event) {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        if (
          payload?.version === STORAGE_VERSION
          && Array.isArray(payload.products)
          && Number(payload.expiresAt) > Date.now()
        ) {
          this.history = uniqueEntries(payload.products.map(normalizeEntry)).slice(0, MAX_PRODUCTS);
          this.dispatchEvent(new CustomEvent('vl:product-compare-history-change', {
            bubbles: true,
            detail: { history: this.history }
          }));
        }
      } catch (error) {
        this.emitError('history-sync-failed', error);
      }
    }

    replaceUrl() {
      if (this.products.length < MIN_PRODUCTS) return;
      window.history.replaceState(
        window.history.state,
        '',
        compareUrl(this.basePath, this.products)
      );
    }

    getCompareUrl(products = this.products) {
      return compareUrl(this.basePath, products);
    }

    analyticsContext() {
      return this.mode === 'pdp' ? 'pdp' : 'compare_page';
    }

    trackAnalytics(eventName, options = {}) {
      this.constructor.pushAnalytics(eventName, {
        context: this.analyticsContext(),
        products: this.products,
        ...options
      });
    }

    trackRenderedView() {
      if (this.products.length < MIN_PRODUCTS) return;
      if (this.mode === 'pdp') {
        if (!this.pdpTrackingPending) return;
        this.trackAnalytics('compare_start', {
          initialSource: this.initialSource,
          action: 'start'
        });
        this.trackAnalytics('compare_view', {
          initialSource: this.initialSource,
          action: 'view'
        });
        this.pdpTrackingPending = false;
        return;
      }
      if (this.viewTracked) return;
      this.trackAnalytics('compare_view', {
        initialSource: this.initialSource,
        action: 'view'
      });
      this.viewTracked = true;
    }

    emitChange(source, changeDetail = {}) {
      this.dispatchEvent(new CustomEvent('vl:product-compare-change', {
        bubbles: true,
        detail: {
          ...changeDetail,
          source,
          products: this.products,
          history: this.history,
          url: this.products.length >= MIN_PRODUCTS ? this.getCompareUrl() : null
        }
      }));
    }

    emitError(code, error = null) {
      this.dispatchEvent(new CustomEvent('vl:product-compare-error', {
        bubbles: true,
        detail: { code, error }
      }));
    }
  }

  customElements.define(TAG_NAME, VLProductCompareManager);
})();
